import { useCallback, useMemo, useReducer } from 'react';
import type { Analytics, EnvironmentData, ExternalLogger } from '@cld/unicorn-api';

import { createReducer } from './reducer';
import type { State } from './state';
import { initialState } from './state';
import type { Action } from './actions';
import type { ConsoleLogger, CookieApi, GetAuthenticationToken, LocationApi, StorageClient, SystemTheme } from './types';
import { analyticsMiddleware } from './middleware/analyticsMiddleware';
import { selectUnicornApi } from './selectors/selectUnicornApi';
import { loggerMiddleware } from './middleware/loggerMiddleware';
import { createSubscriptionMiddleware } from './middleware/subscriptionMiddleware';
import { useDataReload } from './effects/useDataReload';
import { useLogUnicornConfiguration } from './effects/useLogUnicornConfiguration';
import type { AbTestingClientFactory } from '../api/createAbTestingClient';
import { useSubmitGdprLog } from './effects/useSubmitGdprLog';
import type { ExternalLoggerFactory } from '../api/createExternalLoggerFactory';
import { useExternalLoggerClientInfo } from './effects/useExternalLoggerClientInfo';
import { selectGeneratedTheme } from './selectors/selectGeneratedTheme';
import { useApplicationStartupFlow } from './effects/useApplicationStartupFlow';
import { useStoreActiveProductEnvironmentExternalId } from './effects/useStoreActiveProductEnvironmentExternalId';
import { useChangeProductEnvironment } from './effects/useChangeProductEnvironment';
import { useCreateDependencies } from './effects/useCreateDependencies';
import { useReportApplicationStartupError } from './effects/useReportApplicationStartupError';
import { useRedirectToNewNavigation } from './effects/useRedirectToNewNavigation';
import { useLocalStorageMigrations } from './effects/useLocalStorageMigrations';

export const useStateManagement = ({
  analytics,
  abTestingClientFactory,
  devMode,
  environmentData,
  getAuthenticationToken,
  consoleLogger,
  unicornExternalLogger,
  externalLoggerFactory,
  cookieApi,
  localStorageClient,
  systemTheme,
  enableTheming,
  locationApi,
  unicornStorageClient,
}: {
  analytics: Analytics;
  abTestingClientFactory: AbTestingClientFactory;
  devMode: boolean;
  environmentData: EnvironmentData;
  getAuthenticationToken: GetAuthenticationToken;
  consoleLogger: ConsoleLogger;
  unicornExternalLogger: ExternalLogger;
  externalLoggerFactory: ExternalLoggerFactory;
  cookieApi: CookieApi;
  localStorageClient: Storage;
  systemTheme: SystemTheme;
  enableTheming: boolean;
  locationApi: LocationApi;
  unicornStorageClient: StorageClient;
}) => {
  // Setup
  const { subscriptionMiddleware, subscribeToStateChanges, unsubscribeFromStateChanges } = useMemo(() => createSubscriptionMiddleware(), []);
  const reducer = useMemo(() => createReducer({ unicornExternalLogger }), [unicornExternalLogger]);
  const decoratedReducer = useCallback(
    (state: State, action: Action) => {
      const newState = reducer(state, action);
      subscriptionMiddleware(newState);
      analyticsMiddleware(analytics, action);

      if (devMode) {
        loggerMiddleware(consoleLogger, action, state, newState);
      }

      return newState;
    },
    [analytics, consoleLogger, devMode, subscriptionMiddleware, reducer]
  );
  const [state, dispatch] = useReducer(decoratedReducer, initialState);

  // Additional dependencies, which require runtime data such as account/user/product environment
  const { abTestingClientState, consoleApiClient, unicornApiCreateCloudinaryFetchClient } = useCreateDependencies({
    abTestingClientFactory,
    consoleLogger,
    environmentData,
    getAuthenticationToken,
    localStorageClient,
    locationApi,
    unicornExternalLogger,
    state,
  });

  // Side effects
  useRedirectToNewNavigation({ state, locationApi, environmentData, consoleLogger });
  useLocalStorageMigrations({ localStorageClient, unicornStorageClient, consoleLogger });
  useLogUnicornConfiguration({ environmentData, consoleLogger });
  useExternalLoggerClientInfo({ consoleLogger, externalLoggerFactory, subscribeToStateChanges, unsubscribeFromStateChanges });
  useStoreActiveProductEnvironmentExternalId({ consoleLogger, localStorageClient, state });
  useApplicationStartupFlow({
    consoleApiClient,
    dispatch,
    state,
    getPageHref: locationApi.getHref,
    localStorageClient,
    consoleLogger,
    cookieApi,
    systemTheme,
    enableTheming,
  });
  useReportApplicationStartupError({ state, unicornExternalLogger });
  useDataReload({ consoleApiClient, dispatch, state });
  useChangeProductEnvironment({
    consoleApiClient,
    dispatch,
    state,
    consoleLogger,
    environmentData,
    getAuthenticationToken,
    localStorageClient,
    locationApi,
    unicornExternalLogger,
  });
  useSubmitGdprLog({ consoleApiClient, dispatch, state });

  // Selectors (derived data)
  const unicornApi = useMemo(
    () =>
      selectUnicornApi({
        analytics,
        abTestingClientState,
        dispatch,
        environmentData,
        createCloudinaryFetchClient: unicornApiCreateCloudinaryFetchClient,
        subscribeToStateChanges,
        unsubscribeFromStateChanges,
        state,
        consoleLogger,
        externalLoggerFactory,
      }),
    [
      analytics,
      abTestingClientState,
      dispatch,
      environmentData,
      unicornApiCreateCloudinaryFetchClient,
      consoleLogger,
      externalLoggerFactory,
      subscribeToStateChanges,
      unsubscribeFromStateChanges,
      state,
    ]
  );
  const selectedTheme = useMemo(() => selectGeneratedTheme(state), [state]);

  return { dispatch, state, unicornApi, selectedTheme };
};
